import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import {
  Bold, Italic, Underline as UnderlineIcon,
  List, ListOrdered, Quote, Link as LinkIcon,
  Heading1, Heading2, Heading3, Type, Undo2, Redo2, Eraser,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Sanitize HTML that comes in or goes out. Whitelist only the tags/attrs the editor produces.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "u", "s", "code", "pre",
    "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "a", "span",
  ],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOW_DATA_ATTR: false,
};

export function sanitizeRichText(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

function ToolbarBtn({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={cn(
        "h-10 w-10 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors",
        "hover:bg-white/5 hover:text-white",
        active && "bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary",
        disabled && "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground",
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-border mx-0.5" aria-hidden />;
}

export default function RichTextEditor({ value, onChange, placeholder = "Write a description…", minHeight = 160, testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // starter-kit already includes bold/italic/strike/code/lists/blockquote/history
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: sanitizeRichText(value || ""),
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-invert prose-sm max-w-none focus:outline-none",
          "prose-p:my-2 prose-headings:mt-3 prose-headings:mb-2",
          "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5",
          "prose-blockquote:border-l-primary prose-blockquote:my-2",
          "prose-a:text-primary prose-strong:text-white",
          "px-3 py-2.5",
        ),
        "data-placeholder": placeholder,
      },
      // When users paste from ChatGPT/Gemini/Docs, keep the semantic HTML (headings, lists, bold, italic).
      // Tiptap/ProseMirror pipeline runs its schema through, dropping styles it doesn't understand
      // while preserving structural tags — exactly what we want.
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? "" : sanitizeRichText(ed.getHTML());
      onChange?.(html);
    },
  });

  // Keep the editor in sync if `value` changes externally (e.g. loading an existing technique into the form).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = sanitizeRichText(value || "");
    if (incoming && incoming !== current) {
      editor.commands.setContent(incoming, false);
    } else if (!incoming && !editor.isEmpty) {
      editor.commands.clearContent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-border rounded-md bg-background focus-within:ring-1 focus-within:ring-primary/60 focus-within:border-primary/60 transition-shadow overflow-hidden" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-border bg-black/30">
        <ToolbarBtn testid="rte-h1" title="Large heading" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })}>
          <Heading1 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-h2" title="Medium heading" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}>
          <Heading2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-h3" title="Small heading" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}>
          <Heading3 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-body" title="Body text" onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")}>
          <Type className="h-4 w-4" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn testid="rte-bold" title="Bold  (⌘/Ctrl+B)" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}>
          <Bold className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-italic" title="Italic  (⌘/Ctrl+I)" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}>
          <Italic className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-underline" title="Underline  (⌘/Ctrl+U)" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn testid="rte-bullets" title="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}>
          <List className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-ordered" title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-quote" title="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}>
          <Quote className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          testid="rte-link"
          title="Link"
          onClick={() => {
            const prev = editor.getAttributes("link")?.href || "";
            // eslint-disable-next-line no-alert
            const url = window.prompt("Enter URL", prev);
            if (url === null) return;
            if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
            editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
          }}
          active={editor.isActive("link")}
        >
          <LinkIcon className="h-4 w-4" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn testid="rte-clear" title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
          <Eraser className="h-4 w-4" />
        </ToolbarBtn>
        <div className="flex-1" />
        <ToolbarBtn testid="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testid="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo2 className="h-4 w-4" />
        </ToolbarBtn>
      </div>

      {/* Editor surface */}
      <div
        className="max-h-[420px] overflow-y-auto text-sm"
        style={{ minHeight }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

// Renders sanitized rich-text HTML for read-only display, with tailwind `prose` styling.
export function RichTextView({ html, className = "" }) {
  const safe = sanitizeRichText(html || "");
  if (!safe) return null;
  return (
    <div
      data-testid="rich-text-view"
      className={cn(
        "prose prose-invert prose-sm sm:prose-base max-w-none",
        "prose-headings:font-heading prose-headings:tracking-tight",
        "prose-p:leading-relaxed prose-p:text-muted-foreground",
        "prose-li:text-muted-foreground prose-strong:text-white",
        "prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground",
        "prose-a:text-primary hover:prose-a:underline",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

// For places that only need a plain-text preview (e.g. line-clamp on cards).
export function richTextToPlain(html) {
  if (!html) return "";
  const safe = sanitizeRichText(html);
  const doc = new DOMParser().parseFromString(safe, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}
